export type WaitlistGameType = 'texas' | 'omaha';

/**
 * Narrows an engine game-type value to the REST-callable waitlist types.
 * Unity selects gameType from GameData.GameType; callers fall back through
 * `gameType -> game variant` before giving up.
 */
export const isWaitlistGameType = (raw: unknown): raw is WaitlistGameType =>
  raw === 'texas' || raw === 'omaha';

export type WaitlistEntry = {
  id: string;
  playerGuid: string;
  gameId: number;
  gameType: WaitlistGameType;
  position: number;
  waitlistCount: number;
  autoBuyIn: number;
  yourTurn: boolean;
  availableSeats: number;
  waitlistTimeOut: number;
  waitForAcceptStartTime: string | null;
  bumpCount: number;
  createdAt?: string;
  updatedAt?: string;
};

export type WaitlistBroadcastPayload = {
  players: WaitlistEntry[];
};

export type WaitlistNotification = {
  gameId: number;
  gameType: WaitlistGameType;
  message: string;
};

export type WaitlistJoinRequest = {
  gameId: number;
  gameType: WaitlistGameType;
  playerGuid: string;
  autoBuyIn?: number;
};

export type WaitlistLeaveRequest = {
  gameId: number;
  gameType: WaitlistGameType;
  playerGuid: string;
};

export type WaitlistUpdateRequest = {
  gameId: number;
  gameType: WaitlistGameType;
  playerGuid: string;
  autoBuyIn: number;
};

export type WaitlistFetchRequest = {
  gameType: WaitlistGameType;
  playerGuid: string;
};

const SQL_UTC_DATE_TIME = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}(?:\.\d+)?$/;
const HAS_TIMEZONE = /(z|[+-]\d{2}:?\d{2})$/i;

const num = (raw: unknown, fallback = 0): number => {
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  if (typeof raw === 'string' && raw.trim() !== '' && !Number.isNaN(Number(raw)))
    return Number(raw);
  return fallback;
};

const str = (raw: unknown, fallback = ''): string => (typeof raw === 'string' ? raw : fallback);

const bool = (raw: unknown): boolean => raw === true || raw === 'true' || raw === 1 || raw === '1';

const normalizeWaitForAcceptStartTime = (raw: unknown): string | null => {
  if (raw instanceof Date && Number.isFinite(raw.getTime())) {
    return raw.toISOString();
  }

  if (typeof raw === 'number' && Number.isFinite(raw)) {
    const ms = raw < 1_000_000_000_000 ? raw * 1000 : raw;
    return new Date(ms).toISOString();
  }

  if (typeof raw !== 'string') return null;

  const value = raw.trim();
  if (!value) return null;

  if (SQL_UTC_DATE_TIME.test(value)) {
    return `${value.replace(' ', 'T')}Z`;
  }

  if (!HAS_TIMEZONE.test(value) && /^\d{4}-\d{2}-\d{2}T/.test(value)) {
    return `${value}Z`;
  }

  return value;
};

export const mapWaitlistEntry = (raw: unknown): WaitlistEntry | null => {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const waitForAcceptStartTime = normalizeWaitForAcceptStartTime(
    r.waitForAcceptStartTime ?? r.wait_for_accept_start_time
  );
  const gameType = str(r.gameType ?? r.game_type);
  if (gameType !== 'texas' && gameType !== 'omaha') return null;
  return {
    id: str(r.id),
    playerGuid: str(r.playerGuid ?? r.player_guid),
    gameId: num(r.gameId ?? r.game_id),
    gameType,
    position: num(r.position),
    waitlistCount: num(r.waitlistCount ?? r.waitlist_count),
    autoBuyIn: num(r.autoBuyIn ?? r.auto_buy_in),
    yourTurn: bool(r.yourTurn ?? r.your_turn),
    availableSeats: num(r.availableSeats ?? r.available_seats),
    waitlistTimeOut: num(r.waitlistTimeout ?? r.waitlistTimeOut ?? r.waitlist_time_out),
    waitForAcceptStartTime,
    bumpCount: num(r.bumpCount ?? r.bump_count),
    createdAt:
      typeof r.createdAt === 'string'
        ? r.createdAt
        : typeof r.created_at === 'string'
        ? r.created_at
        : undefined,
    updatedAt:
      typeof r.updatedAt === 'string'
        ? r.updatedAt
        : typeof r.updated_at === 'string'
        ? r.updated_at
        : undefined
  };
};

export const mapWaitlistBroadcast = (raw: unknown): WaitlistEntry[] => {
  if (!raw || typeof raw !== 'object') return [];
  const players = (raw as { players?: unknown }).players;
  if (!Array.isArray(players)) return [];
  return players.map(mapWaitlistEntry).filter((e): e is WaitlistEntry => e !== null);
};

export const mapWaitlistNotification = (raw: unknown): WaitlistNotification | null => {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const gameType = str(r.gameType ?? r.game_type);
  if (gameType !== 'texas' && gameType !== 'omaha') return null;
  return {
    gameId: num(r.gameId ?? r.game_id),
    gameType,
    message: str(r.message)
  };
};
